import { UrlParamKeys } from '../../app/UrlParamKeys';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { FeatureFlags } from '../FeatureFlags';
import { getBooleanParamValue, getPathFromUrl, isMetricUrl, isRootPath, isSignInPath, isVizUrl } from '../helpers/UrlHelpers';
import { Module } from '../Module';
import { WindowLocationService } from './WindowLocationService';

export class MobileAppService {

  public static $inject = [
    '$translate',
    '$cookies',
    'WindowLocationService'
  ];

  constructor(private $translate: ng.translate.ITranslateService,
    private $cookies: ng.cookies.ICookiesService,
    private windowLocationService: WindowLocationService) { }

  public static readonly HIDE_APP_BANNER_COOKIE = 'hideAppBanner';
  // This parameter uses a colon prefix because it's the exact same parameter as used by vizclient.
  public static readonly DEEP_LINKING_DISABLED_PARAM = ':deepLinkingDisabled';
  private static readonly DISABLE_APP_BANNER_PARAM = 'disableAppBanner';
  /**
   * Number of milliseconds to wait for the app to open before redirecting to the app store
   */
  public static readonly REDIRECT_TO_APP_STORE_DELAY_IN_MS = 1000;
  /**
   * Number of milliseconds to wait for the app to open before reloading the page with deep linking disabled
   */
  public static readonly RELOAD_WITH_DEEP_LINKING_DISABLED_PARAM_DELAY_IN_MS = 1;

  /**
   * Checks if we can deep link from this page to the mobile app.
   * This is used to show the custom app banner on this page on mobile browsers, and also to deep link to the app.
   */
  private static canDeepLinkToApp(url: string) {
    return (UserAgentSupport.isMobileBrowser() // Mobile browsers only
      || MobileAppService.isIpadInDesktopMode() // and special case for ipad os
    )
      && (isSignInPath(getPathFromUrl(url)) // sign-in and sign-in-site-selection pages
        || isVizUrl(url) // viz aka view page, e.g., 'http://hostname/#/views/Regional/Obesity'
        || isMetricUrl(url) // metric page, e.g., 'https://us-west-2a.online.tableau.com/#/site/alpodev/metrics/609'
      );
  }

  /**
   * Checks if we should show the custom app banner on mobile browsers.
   * We show the banner only on those pages to which we can deep link into the mobile app.
   * We don't show the banner if the banner has been dismissed by the user within the past 7 days.
   */
  public shouldShowAppBanner(url: string) {
    const disableAppBannerFlag = getBooleanParamValue(url, MobileAppService.DISABLE_APP_BANNER_PARAM);

    return !disableAppBannerFlag // used by mobile app for web auth
      && FeatureFlags.customAppBanner // Feature flag
      && MobileAppService.canDeepLinkToApp(url)
      && !this.$cookies.get(MobileAppService.HIDE_APP_BANNER_COOKIE);
  }

  /**
   * Creates the deeplink. It is "tableau://[url to which we are supposed to get redirected]"
   * If there is no redirect url, then just use "tableau://[current url]"
   */
  public createDeepLink(redirect: string, externalRedirect: string) {
    let deepLink = null;
    if (externalRedirect) {
      deepLink = 'tableau://' + this.windowLocationService.getHost() + externalRedirect;
    } else {
      const protocol = this.windowLocationService.getProtocol();
      deepLink = this.windowLocationService.getHref();
      deepLink = deepLink.replace(protocol, 'tableau:');
      if (redirect) {
        deepLink = deepLink.split('#')[0] + '#' + redirect;
      }
    }
    return deepLink;
  }

  private static isTouchEnabled() {
    return !!('ontouchstart' in window);
  }

  /**
   * TFSID 940092 - As of iOS 13, Safari on iPads will impersonate a desktop browser by default.
   * This makes it impossible to detect iPads by user agent alone. To get around this we infer 
   * iPads in desktop browser mode by checking for a Macintosh user agent string and touch support. 
   * TFSID:960184 We also have to check that the request is not coming from a tableau application
   * since the webview on a tableau desktop (mac) used for authentication can meet the first two
   * conditionals. They have touch events due to it using QWebView. So we exclude this case by
   * making sure "Tableau" is not in the user agent string
   */
  private static isIpadInDesktopMode() {
    return MobileAppService.isTouchEnabled() && UserAgentSupport.isSafariOnMac() && !UserAgentSupport.isTableau();
  }

  public maybeRedirectToApp(deepLink: string) {
    // This will trigger the browser to open the app if it's installed.
    (window as any).location = deepLink;

    // We redirect to the app store. This is delayed to give the browser a chance to
    // redirect to the app store.
    setTimeout(() => {
      if (UserAgentSupport.isIOS()) {
        // This additional dialogue is necessary for iOS because iOS prompts a native dialogue
        // to open the app. Without the dialogue below, the browser will redirect to the app store
        // before the user is able to respond to the native dialogue. The dialogue below will show up
        // behind the native dialogue - this unusual behavior is expected.
        if (confirm(this.$translate.instant('redirectToAppStore'))) {
          (window as any).location = 'https://itunes.apple.com/app/tableau-mobile/id434633927';
        }
      } else if (UserAgentSupport.isAndroid()) {
        (window as any).location = 'https://play.google.com/store/apps/details?id=com.Tableau.TableauApp';
      }
    }, MobileAppService.REDIRECT_TO_APP_STORE_DELAY_IN_MS);
  }

  /**
   * We try to deep link only from those pages where we show the app banner.
   * In addition, we deep link even if the user has previously dismissed the app banner.
   * We try to redirect to the mobile app using tableau:// scheme when the page is first loaded.
   * We do not redirect on subsequent reloads of the page.
   * For details, see https://reviewboard.prod.tableautools.com/r/253096/diff/7/#7
   * Note: This method was converted into an instance method (instead of being a static method) to make
   * the pipeline pass in 2020.1 branch. The root cause of the failure is still unknown, but this does fix the problem.
   * See https://gitlab.tableausoftware.com/browser-clients/vizportalclient/merge_requests/4897#note_1060085
   * @returns true if it is going to try to deep link into the app, false otherwise
   */
  public tryDeepLinkToApp(locService: WindowLocationService): boolean {
    const url = locService.getHref();
    if (FeatureFlags.enableDeepLinking // feature flag is on
      && MobileAppService.canDeepLinkToApp(url) // this is a page we can deep link to the mobile app
      && !UserAgentSupport.isFirefox() // is not Firefox, see TFSID 1021853
      && !MobileAppService.containsParamsThatDisableDeepLinking(url) // url does not have params that disable deep linking
    ) {
      const deepLink = url.replace(locService.getProtocol(), 'tableau:');

      // Reload the page, with deep linking disabled so that we don't go in circles.
      // This is delayed to give the browser a chance to redirect to the app.
      const deepLinkDisabled = MobileAppService.addParamToDisableDeepLinking(url);
      setTimeout(() => MobileAppService.redirectToUrl(locService, deepLinkDisabled), MobileAppService.RELOAD_WITH_DEEP_LINKING_DISABLED_PARAM_DELAY_IN_MS);

      // This will trigger the browser to open the app if it's installed.
      MobileAppService.redirectToUrl(locService, deepLink);
      return true;
    }
    return false;
  }

  public static containsParamsThatDisableDeepLinking(url: string): boolean {
    return getBooleanParamValue(url, MobileAppService.DEEP_LINKING_DISABLED_PARAM) ||
      getBooleanParamValue(url, UrlParamKeys.ClosePopupWhenDone) ||
      getBooleanParamValue(url, UrlParamKeys.Embedded);
  }

  public static addParamToDisableDeepLinking(url: string): string {
    const newUrl = new URL(url);
    const searchParams = new URLSearchParams(newUrl.search);
    searchParams.set(MobileAppService.DEEP_LINKING_DISABLED_PARAM, 'y');
    newUrl.search = searchParams.toString();
    return newUrl.href;
  }

  private static redirectToUrl(locService: WindowLocationService, url: string) {
    locService.navigateToWithoutAddingToHistory(url);
  }
}

Module.instance().service('MobileAppService', MobileAppService);
